import LanguagePreferences from "./language-preferences";

export default class Util {

	public static readonly SEARCH_CONFIG = {
		'coveoSearchId': '#search',
		'removeFromResultTitle': [' \\| TI.com.*', ' \\| Texas Instruments.*']
	}

	public static makeAnalyticsCall(eventCategory: string, eventAction: string, eventLabel: string, retryCount: number = 0) {
		if (typeof (window as any)._tiAnalyticsTrack !== 'undefined') {
			(window as any)._tiAnalyticsTrack(eventCategory, eventAction, eventLabel);
		} else if (retryCount < 5) {
			retryCount++;
			setTimeout(() => {
				this.makeAnalyticsCall(eventCategory, eventAction, eventLabel, retryCount);
			}, 1250);
		}
	}

	public static boldText(text: string, termsToBold: string[]) {
		termsToBold.forEach(term => {
			// assemble regex to find whole word matches, ignore case
			const textRegex = new RegExp(`\\b(${term})\\b`, 'ig');
			text = text.replace(textRegex, (match) => `<b>${match}</b>`)
		})
		return text;
	}

	public static async getLangPref(lang: string): Promise<string> {
		await LanguagePreferences.resolveUserLangPref().then(resolvedLang => {
			lang = resolvedLang
		})
		return lang;
	}

	public static languageServer(lang: string): string {
		if ((window as any).TISearchData) {
			const TISearchData = (window as any).TISearchData;
			if (TISearchData.language_servers) {
				return TISearchData.language_servers[lang] ? TISearchData.language_servers[lang].replace(/\//g, '') : '';
			}
		}
		return '';
	}

	/**
	 * Helper method to add a query string to a URL
	 * @param url
	 * @param queryString
	 */
	public static addQueryParams(url: string, queryString: string): string {
		if (queryString) {
			// base?query#hash
			const basePart: string = url.split('?')[0].split('#')[0];
			const hashPart: string = (url.indexOf('#') < 0) ? '' : url.split('#')[1];
			let queryPart: string = (url.indexOf('?') < 0) ? '' : url.split('?')[1].split('#')[0];
			queryPart = queryPart ? `${queryPart}&${queryString}` : queryString;
			url = `${basePart}${queryPart ? '?' : ''}${queryPart}${hashPart ? '#' : ''}${hashPart}`;
		}
		return url;
	}

	/**
	 * Helper method to add a query string parameter with value to a URL
	 * @param url
	 * @param name
	 * @param value
	 */
	public static addQueryParam(url: string, name: string, value: string): string {
		if (name && value != undefined) {
			return Util.addQueryParams(url, `${name}${value ? '=' : ''}${value}`);
		}
		return url;
	}

	/**
	 * Helper method to remove a query string parameter from a URL
	 * @param url
	 * @name name
	 */
	public static removeQueryParam(url: string, name: string): string {
		if (name) {
			// base?query#hash
			const basePart: string = url.split('?')[0].split('#')[0];
			const hashPart: string = (url.indexOf('#') < 0) ? '' : url.split('#')[1];
			let queryPart: string = (url.indexOf('?') < 0) ? '' : url.split('?')[1].split('#')[0];
			queryPart = queryPart.split('&').filter((p) => { return p.split('=')[0] != name; }).join('&');
			url = `${basePart}${queryPart ? '?' : ''}${queryPart}${hashPart ? '#' : ''}${hashPart}`;
		}
		return url;
	}

	/**
	 * Helper method to get the value of a query string parameter in a URL
	 * @param url
	 * @param name
	 */
	public static getQueryParam(url: string, name: string): string {
		let value: string = undefined;
		if (name && url.indexOf('?') >= 0) {
			url.split('?')[1].split('#')[0].split('&').forEach((p) => {
				const nvp: string[] = p.split('=');
				if (nvp[0] == name) value = (nvp.length > 1) ? nvp[1] : '';
			});
		}
		return value;
	}

	/**
	 * Helper method to set the value of a query string parameter in a URL
	 * @param url
	 * @param name
	 * @param value
	 */
	public static setQueryParam(url: string, name: string, value: string): string {
		if (name) {
			if (Util.getQueryParam(url, name) == undefined) {
				return Util.addQueryParams(url, `${name}=${value}`);
			}
			// base?query#hash
			const basePart: string = url.split('?')[0].split('#')[0];
			const hashPart: string = (url.indexOf('#') < 0) ? '' : url.split('#')[1];
			let queryPart: string = (url.indexOf('?') < 0) ? '' : url.split('?')[1].split('#')[0];
			// update value in place
			queryPart = queryPart.split('&').map((p) => {
				const nvp: string[] = p.split('=');
				return (nvp[0] != name) ? p : (value == undefined) ? '' : `${nvp[0]}${value ? '=' : ''}${value}`;
			}).join('&');
			url = `${basePart}${queryPart ? '?' : ''}${queryPart}${hashPart ? '#' : ''}${hashPart}`;
		}
		return url;
	}

	/**
	 * Helper method to add a hash string to a URL
	 * @param url
	 * @param queryString
	 */
	public static addHashParams(url: string, hashString: string): string {
		if (hashString) {
			// base?query#hash
			const basePart: string = url.split('?')[0].split('#')[0];
			const queryPart: string = (url.indexOf('?') < 0) ? '' : url.split('?')[1].split('#')[0];
			let hashPart: string = (url.indexOf('#') < 0) ? '' : url.split('#')[1];
			hashPart = hashPart ? `${hashPart}&${hashString}` : hashString;
			url = `${basePart}${queryPart ? '?' : ''}${queryPart}${hashPart ? '#' : ''}${hashPart}`;
		}
		return url;
	}

	/**
	 * Helper method to add a hash parameter with value to a URL
	 * @param url
	 * @param name
	 * @param value
	 */
	public static addHashParam(url: string, name: string, value: string): string {
		if (name && value != undefined) {
			return Util.addHashParams(url, `${name}${value ? '=' : ''}${value}`);
		}
		return url;
	}

	/**
	 * Helper method to remove a hash
	 * @name name
	 */
	public static removeHashParam(url: string, name: string): string {
		if (name) {
			// base?query#hash
			const basePart: string = url.split('?')[0].split('#')[0];
			const queryPart: string = (url.indexOf('?') < 0) ? '' : url.split('?')[1].split('#')[0];
			let hashPart: string = (url.indexOf('#') < 0) ? '' : url.split('#')[1];
			hashPart = hashPart.split('&').filter((p) => { return p.split('=')[0] != name; }).join('&');
			url = `${basePart}${queryPart ? '?' : ''}${queryPart}${hashPart ? '#' : ''}${hashPart}`;
		}
		return url;
	}

	/**
	 * Helper method to get the value of a hash parameter in a URL
	 * @param url
	 * @param name
	 */
	public static getHashParam(url: string, name: string): string {
		let value: string = undefined;
		if (name && url.indexOf('#') >= 0) {
			url.split('#')[1].split('&').forEach((p) => {
				const nvp: string[] = p.split('=');
				if (nvp[0] == name) value = (nvp.length > 1) ? nvp[1] : '';
			});
		}
		return value;
	}

	/**
	 * Helper method to set the value of a hash parameter in a URL
	 * @param url
	 * @param name
	 * @param value
	 */
	public static setHashParam(url: string, name: string, value: string): string {
		if (name) {
			if (Util.getHashParam(url, name) == undefined) {
				return Util.addHashParams(url, `${name}=${value}`);
			}
			// base?query#hash
			const basePart: string = url.split('?')[0].split('#')[0];
			const queryPart: string = (url.indexOf('?') < 0) ? '' : url.split('?')[1].split('#')[0];
			let hashPart: string = (url.indexOf('#') < 0) ? '' : url.split('#')[1];
			// update value in place
			hashPart = hashPart.split('&').map((p) => {
				const nvp: string[] = p.split('=');
				return (nvp[0] != name) ? p : (value == undefined) ? '' : `${nvp[0]}${value ? '=' : ''}${value}`;
			}).join('&');
			url = `${basePart}${queryPart ? '?' : ''}${queryPart}${hashPart ? '#' : ''}${hashPart}`;
		}
		return url;
	}

	public static cleanseSearchTerm(searchTerm: string): string {
		try {
			searchTerm = decodeURIComponent(searchTerm);
		} catch (err) {
		}
		searchTerm = searchTerm.replace(/[\._]/gi, " ");
		searchTerm = searchTerm.replace(/file:/ig, " ");
		searchTerm = searchTerm.replace(/ftp:/ig, " ");
		searchTerm = searchTerm.replace(/data:/ig, " ");
		searchTerm = searchTerm.replace(/http:/ig, " ");
		searchTerm = searchTerm.replace(/c:/g, " ");
		searchTerm = searchTerm.replace(/\s+/g, " ");
		searchTerm = searchTerm.replace(/[`~!@#$%^&*()|+\=?;'",<>\{\}\[\]]/gi, "");
		searchTerm = searchTerm.replace(/null/ig, " ");
		searchTerm = searchTerm.replace(/•/ig, " ");
		searchTerm = searchTerm.trim();
		searchTerm = (searchTerm.length > 250) ? searchTerm.substring(0, 250) : searchTerm;
		return searchTerm;
	}

	public static getLocale(): string {
		const win = (typeof (window as any) !== 'undefined' ? window : {}) as any;
		if ((win as any).TISearchData && (win as any).TISearchData.locale) {
			return (win as any).TISearchData.locale;
		}
		else {
			return 'en-US';
		}

	}

	public static rewriteUrlNonprod(originalUrl: string, locale: string): string {
		const prodHosts: string[] = ['www.ti.com', 'www.ti.com.cn', 'www.tij.co.jp'];
		if (prodHosts.includes(window.location.host)) return originalUrl;
		const url = document.createElement('a');
		url.href = originalUrl;
		if (!prodHosts.includes(url.hostname)) return originalUrl;
		let rewrittenHost: string = window.location.host;
		// special logic for JP store
		if (locale == 'ja-JP' && url.host == 'www.ti.com' && url.pathname.startsWith('/store/')) {
			rewrittenHost = Util.languageServer('en-US');
		}
		let rewrittenUrl: string = window.location.protocol + '//' + rewrittenHost;
		rewrittenUrl += url.pathname.replace(/(^\/?)/, '/') + url.search + url.hash;
		return rewrittenUrl;
	}

	/**
	 * decode HTML characters
	 * @param locale
	 */
	public static decodeHtml(input): string {
		// @ts-ignore
		return input.replace(/&amp;/g, '&').replace(/&#(\d+);/g, function (match, code) {
			return String.fromCharCode(code);
		});
	}

	/**
	 * Check that locale is valid. If not, return en-US default locale.
	 * @param locale - the locale to validate
	 * @returns String validated locale
	 */
	public static getValidLocale(locale: string): string {
		return ['en-US', 'es-MX', 'de-DE', 'ja-JP', 'ko-KR', 'ru-RU', 'zh-CN', 'zh-TW'].includes(locale) ? locale : 'en-US';
	}

	/**
	 * Get domain from URL
	 * @param domainToUse - the users window.location.hostname
	 * @returns String domain
	 */
	public static getDomain(domainToUse: string = window.location.hostname): string {
		if (domainToUse === "localhost") {
			const port = window.location.port
				? ":" + window.location.port
				: "";
			return domainToUse + port;
		}
		if (!domainToUse.match(/^(www\.)/)) {
			const env = domainToUse.match(/^((www\-|perf\-|login\-|mobile\-)[a-z0-9\.\-]+\.)?(ti\.com\.cn|tij\.co\.jp|ti\.com)/);
			domainToUse = !env
				? "www.ti.com"
				: env[0].match(/^(www[\-]?|perf\-)/)   // if www, lower env or perf, just go ahead and use it
					? env[0]
					: env[0].match(/^(mobile\-)/)   // direct mobile-dev.aws.ti.com to perf
						? "www-perf." + env[3]
						: "www." + env[3];
		}
		return domainToUse;
	}

	/**
	 * Format URL / replace template vars
	 * @param url
	 * @param replace
	 * @param queryString
	 */
	public static formatUrl(url: string, replace: object, queryString: string = ''): string {
		let formatted: string = url;
		if (queryString) {
			formatted = Util.addQueryParams(formatted, queryString);
		}
		const props = Object.getOwnPropertyNames(replace);
		props.forEach(function (prop: string) {
			const pattern: RegExp = new RegExp(`{${prop}}`, 'g');
			formatted = formatted.replace(pattern, replace[prop]);
		});
		return formatted;
	}

	public static getCookie(name, defaultValue = null) {
		const v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
		return v ? decodeURIComponent(v[2]) : defaultValue;
	}

	public static setCookie(name, value) {
		document.cookie = name + '=' + value + ';path=/;';
	}

	public static wait(ms: number = 1000): Promise<void> {
		return new Promise<void>(resolve => {
			setTimeout(function () { resolve(); }, ms || 1000);
		});
	}

	public static async waitForTrue(fn, maxRetries: number = 100, nFrequency: number = 100): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			let retries: number = maxRetries || 100;
			do {
				if (fn()) break;
				await Util.wait(nFrequency || 100);
			} while (retries--);
			if (fn()) return resolve();
			return reject(new Error('waitForTrue expired unresolved'));
		});
	}

	public static globalExists(varName: string): boolean {
		let last = typeof (window as any) !== 'undefined' ? window : {} as Window;
		const parts: string[] = varName.split('.');
		for (let i = 0; i < parts.length; i++) {
			if (!last.hasOwnProperty(parts[i])) return false;
			last = last[parts[i]];
		}
		return true;
	}

	public static waitUntilGlobalExists(varName: string, maxRetries: number = 100, nFrequency: number = 100): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			Util.waitForTrue(() => { return Util.globalExists(varName); }, maxRetries, nFrequency)
				.then(() => { if (Util.globalExists(varName)) return resolve(); })
				.catch(() => { return reject('waitUntilGlobalExists(' + varName + ') expired unresolved'); });
		});
	}

	private static readonly _SAFARI_REGEXP: RegExp = /^((?!chrome|android|CriOS|FxiOS|YaBrowser).)*safari/i;
	public static isSafari(): boolean {
		const result: boolean = Util._SAFARI_REGEXP.test(navigator.userAgent);
		if (result) console.log('isSafari:' + result);
		return result;
	}

}
